import React, { useEffect, useMemo, useState } from 'react';
import './Dropdown.css';
import { apiCall } from '../utils/auth';

const formatBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const formatQtd = (v) => Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 3 });
const COR = 'rgb(235, 133, 0)';

// Matéria-prima por produção: escolhe a produção no grid e vê, num pop-up,
// o que já foi usado, o que o orçamento dela ainda tem reservado e o total.
// No final, os orçamentos que ainda não geraram produção (só têm matéria-prima reservada).
export default function MateriaPrimaDropdown() {
  const [producoes, setProducoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [filtro, setFiltro] = useState('');
  // Campo do filtro: 'todos' | 'producao' | 'orcamento' | 'cliente'
  const [campoFiltro, setCampoFiltro] = useState('todos');
  const [orcamentosSemProducao, setOrcamentosSemProducao] = useState([]);

  // Item aberto no pop-up: { tipo: 'producao' | 'orcamento', dados }
  const [aberto, setAberto] = useState(null);
  const [resumo, setResumo] = useState(null);
  const [carregandoResumo, setCarregandoResumo] = useState(false);
  const [erroResumo, setErroResumo] = useState('');

  useEffect(() => {
    carregarProducoes();
    carregarOrcamentosSemProducao();
  }, []);

  // Avisa o widget de ajuda se o pop-up está aberto (ele mostra o "Resumo deste formulário" em vez de fechar o pop-up)
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('materia-prima-popup-state', { detail: { formularioAberto: !!aberto } }));
  }, [aberto]);
  useEffect(() => () => {
    window.dispatchEvent(new CustomEvent('materia-prima-popup-state', { detail: { formularioAberto: false } }));
  }, []);

  // Fechar pelo widget de ajuda
  useEffect(() => {
    const fechar = () => fecharResumo();
    window.addEventListener('materia-prima-fechar-todos-popups', fechar);
    return () => window.removeEventListener('materia-prima-fechar-todos-popups', fechar);
  }, []);

  const carregarProducoes = async () => {
    try {
      setLoading(true);
      const data = await apiCall('Producoes');
      const lista = Array.isArray(data) ? data : [];
      setProducoes(lista);
      setErro('');

      // Vindo da tela de Produções (?filtroCampo=producao&filtroValor=ID): abre direto o pop-up dela
      const params = new URLSearchParams(window.location.search);
      if (params.get('filtroCampo') === 'producao' && params.get('filtroValor')) {
        const id = params.get('filtroValor');
        const p = lista.find(x => String(idDe(x)) === String(id));
        abrirResumo(p || { producaoId: id });
      }
    } catch (e) {
      console.error('Erro ao carregar projetos:', e);
      setErro('Erro ao carregar os projetos.');
      setProducoes([]);
    } finally {
      setLoading(false);
    }
  };

  const carregarOrcamentosSemProducao = async () => {
    try {
      const data = await apiCall('MateriaPrima/orcamentos-sem-producao');
      setOrcamentosSemProducao(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Erro ao carregar orçamentos sem projeto:', e);
      setOrcamentosSemProducao([]);
    }
  };

  const idDe = (p) => p?.producaoId ?? p?.ProducaoId ?? p?.id ?? p?.Id;

  const abrirResumo = (p) => carregarResumo({ tipo: 'producao', dados: p }, `MateriaPrima/resumo-producao/${idDe(p)}`);
  const abrirOrcamento = (o) => carregarResumo({ tipo: 'orcamento', dados: o }, `MateriaPrima/resumo-orcamento/${o.orcamentoId}`);

  const carregarResumo = async (item, endpoint) => {
    setAberto(item);
    setResumo(null);
    setErroResumo('');
    setCarregandoResumo(true);
    try {
      const data = await apiCall(endpoint);
      setResumo(data);
    } catch (e) {
      console.error('Erro ao carregar matérias-primas do projeto:', e);
      setErroResumo('Não foi possível carregar as matérias-primas.');
    } finally {
      setCarregandoResumo(false);
    }
  };

  const fecharResumo = () => {
    setAberto(null);
    setResumo(null);
  };

  const filtradas = useMemo(() => {
    const termo = filtro.trim().toLowerCase();
    if (!termo) return producoes;
    const contem = (v) => String(v ?? '').toLowerCase().includes(termo);
    return producoes.filter(p => {
      if (campoFiltro === 'producao') return String(idDe(p) ?? '') === termo;
      if (campoFiltro === 'orcamento') return String(p.orcamentoId ?? p.OrcamentoId ?? '') === termo;
      if (campoFiltro === 'cliente') return contem(p.clienteNome ?? p.ClienteNome);
      return [
        idDe(p), p.titulo ?? p.Titulo, p.clienteNome ?? p.ClienteNome, p.status ?? p.Status,
        p.contratosId ?? p.ContratosId, p.orcamentoId ?? p.OrcamentoId
      ].some(contem);
    });
  }, [producoes, filtro, campoFiltro]);

  const orcamentosFiltrados = useMemo(() => {
    const termo = filtro.trim().toLowerCase();
    if (!termo) return orcamentosSemProducao;
    const contem = (v) => String(v ?? '').toLowerCase().includes(termo);
    return orcamentosSemProducao.filter(o => {
      if (campoFiltro === 'producao') return false;
      if (campoFiltro === 'orcamento') return String(o.orcamentoId ?? '') === termo;
      if (campoFiltro === 'cliente') return contem(o.clienteNome);
      return [o.orcamentoId, o.codigo, o.clienteNome, o.status, o.contratoId].some(contem);
    });
  }, [orcamentosSemProducao, filtro, campoFiltro]);

  const ehOrcamento = aberto?.tipo === 'orcamento';

  const usadas = resumo?.usadas ?? [];
  // Perdidas/danificadas na produção: não foram usadas nem voltaram ao estoque (contam como gasto, igual ao usado)
  const perdidas = resumo?.perdidas ?? [];
  // Reservado inicialmente = previsto do orçamento (não diminui com o uso); sem histórico, o que ainda está reservado
  const reservadoInicial = (resumo?.reservadoInicial?.length ? resumo.reservadoInicial : resumo?.reservadas) ?? [];
  const total = resumo?.total ?? [];

  return (
    <div className="container-geral fundo-branco" style={{ minHeight: '100vh', background: '#f0f0f0', padding: 20 }}>
      <h2 style={{ textAlign: 'center', color: COR, marginBottom: 20 }}>📦 Matéria-Prima por Projeto</h2>

      <div className="filtro-container">
        <div className="filtro-wrapper">
          <input
            type={campoFiltro === 'producao' || campoFiltro === 'orcamento' ? 'number' : 'text'}
            placeholder={{
              todos: 'Buscar por ID, título, cliente, status, contrato ou orçamento...',
              producao: 'Digite o ID do projeto...',
              orcamento: 'Digite o ID do orçamento...',
              cliente: 'Digite o nome do cliente...'
            }[campoFiltro]}
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            className="filtro-input"
          />
          <select
            value={campoFiltro}
            onChange={(e) => { setCampoFiltro(e.target.value); setFiltro(''); }}
            className="materia-prima-filtro-campo"
            aria-label="Filtrar por"
          >
            <option value="todos">Todos os campos</option>
            <option value="producao">Projeto ID</option>
            <option value="orcamento">Orçamento ID</option>
            <option value="cliente">Nome do cliente</option>
          </select>
        </div>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>Carregando projetos...</div>}
      {erro && <div style={{ textAlign: 'center', padding: 20, color: '#b00020', background: '#fdecea', borderRadius: 6, margin: '20px 0' }}>{erro}</div>}

      {!loading && !erro && (
        filtradas.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#666', background: 'white', marginTop: 20, borderRadius: 10 }}>
            {filtro ? 'Nenhum projeto encontrado para a busca' : 'Nenhum projeto cadastrado'}
          </div>
        ) : (
          <div className="materia-prima-producoes-grid">
            {filtradas.map((p, idx) => {
              const id = idDe(p);
              const status = p.statusNome ?? p.StatusNome ?? p.status ?? p.Status ?? '';
              return (
                <button type="button" key={id ?? idx} className="materia-prima-producao-card" onClick={() => abrirResumo(p)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: COR }}>Projeto #{id}</span>
                    {status && <span className="materia-prima-producao-status">{status}</span>}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#333', lineHeight: 1.3 }}>{p.titulo ?? p.Titulo ?? 'Sem título'}</div>
                  <div style={{ fontSize: 13, color: '#666', display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <span><strong>Cliente:</strong> {p.clienteNome ?? p.ClienteNome ?? '—'}</span>
                    <span><strong>Contrato:</strong> {p.contratosId ?? p.ContratosId ?? '—'} · <strong>Orçamento:</strong> {p.orcamentoId ?? p.OrcamentoId ?? '—'}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )
      )}

      {!loading && orcamentosFiltrados.length > 0 && (
        <div className="materia-prima-orcamentos-secao">
          <h3>📝 Orçamentos sem projeto iniciado</h3>
          <p>Matéria-prima reservada em orçamentos que ainda não viraram projeto.</p>
          <div className="materia-prima-producoes-grid">
            {orcamentosFiltrados.map(o => (
              <button type="button" key={o.orcamentoId} className="materia-prima-producao-card materia-prima-orcamento-card" onClick={() => abrirOrcamento(o)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#1565c0' }}>Orçamento #{o.orcamentoId}</span>
                  {o.status && <span className="materia-prima-producao-status materia-prima-orcamento-status">{o.status}</span>}
                </div>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#333', lineHeight: 1.3 }}>{o.codigo || 'Sem código'}</div>
                <div style={{ fontSize: 13, color: '#666', display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <span><strong>Cliente:</strong> {o.clienteNome || '—'}</span>
                  <span><strong>Contrato:</strong> {o.contratoId ? `#${o.contratoId} (${o.contratoStatus || 'sem status'})` : 'ainda não gerado'}</span>
                  <span><strong>Itens reservados:</strong> {o.itensReservados}</span>
                </div>
                <div className="materia-prima-orcamento-aviso">Orçamento sem projeto iniciado</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {aberto && (
        <div className="modal-overlay" onClick={fecharResumo} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 95000, padding: 16 }}>
          <div className="modal materia-prima-resumo-modal" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
              <div>
                {ehOrcamento ? (
                  <>
                    <h3 style={{ margin: 0, color: '#1565c0' }}>Orçamento #{aberto.dados.orcamentoId}</h3>
                    <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                      {[aberto.dados.codigo, aberto.dados.clienteNome, 'Sem projeto iniciado'].filter(Boolean).join(' · ')}
                    </div>
                  </>
                ) : (
                  <>
                    <h3 style={{ margin: 0, color: COR }}>Projeto #{idDe(aberto.dados)}</h3>
                    <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                      {resumo?.titulo ?? aberto.dados.titulo ?? aberto.dados.Titulo ?? ''}
                      {resumo?.orcamentoId ? ` · Orçamento #${resumo.orcamentoId}` : ''}
                    </div>
                  </>
                )}
              </div>
              <button onClick={fecharResumo} aria-label="Fechar" title="Fechar" className="materia-prima-fechar" style={{ backgroundColor: ehOrcamento ? '#1565c0' : COR }}>×</button>
            </div>

            {carregandoResumo && <div style={{ textAlign: 'center', padding: 30, color: '#666' }}>Carregando matérias-primas...</div>}
            {erroResumo && <div style={{ textAlign: 'center', padding: 16, color: '#b00020', background: '#fdecea', borderRadius: 6 }}>{erroResumo}</div>}

            {resumo && !carregandoResumo && (
              <>
                <Secao
                  id="mp-resumo-reservadas"
                  titulo="📌 Reservadas no orçamento"
                  vazio={resumo.orcamentoId
                    ? 'O orçamento não tem matéria-prima reservada.'
                    : 'Este projeto não tem orçamento vinculado.'}
                >
                  {reservadoInicial.length > 0 && <TabelaItens itens={reservadoInicial} mostrarTotal />}
                </Secao>

                {!ehOrcamento && (
                  <Secao id="mp-resumo-usadas" titulo="🔨 Usadas no projeto" vazio="Nenhuma matéria-prima usada neste projeto ainda.">
                    {usadas.length > 0 && (
                      <TabelaItens itens={usadas} mostrarOrigem />
                    )}
                  </Secao>
                )}

                {!ehOrcamento && (
                  <Secao id="mp-resumo-perdidas" titulo="⚠️ Perdidas no projeto" vazio="Nenhuma matéria-prima perdida ou danificada neste projeto.">
                    {perdidas.length > 0 && (
                      <TabelaItens itens={perdidas} mostrarOrigem mostrarTotal />
                    )}
                  </Secao>
                )}

                {!ehOrcamento && (

                <Secao id="mp-resumo-total" titulo="📊 Total (usado + perdido + reservado restante)" vazio="Nenhuma matéria-prima neste projeto.">
                  {total.length > 0 && (
                    <div className="materia-prima-tabela-wrapper">
                      <table className="materia-prima-tabela">
                        <thead>
                          <tr><th>Material</th><th>Usado</th><th>Perdido</th><th>Reservado restante</th><th>Total</th><th>Valor</th></tr>
                        </thead>
                        <tbody>
                          {total.map((t, i) => {
                            // Usou (ou perdeu) mais do que o orçamento reservou: o excedente saiu do estoque
                            const inicial = (resumo.reservadoInicial || []).find(r => r.material === t.material);
                            const passou = inicial && !ehOrcamento ? Number(t.usado || 0) + Number(t.perdido || 0) - Number(inicial.quantidade || 0) : 0;
                            return (
                            <tr key={i}>
                              <td>{t.material}</td>
                              <td>
                                {formatQtd(t.usado)} {t.unidade || ''}
                                {passou > 0 && (
                                  <div style={{ fontSize: 11, fontWeight: 700, color: '#c62828' }}>
                                    passou {formatQtd(passou)} do reservado
                                  </div>
                                )}
                              </td>
                              <td style={Number(t.perdido || 0) > 0 ? { color: '#c62828', fontWeight: 600 } : undefined}>{formatQtd(t.perdido || 0)} {t.unidade || ''}</td>
                              <td>{formatQtd(t.reservado)} {t.unidade || ''}</td>
                              <td><strong>{formatQtd(t.quantidade)} {t.unidade || ''}</strong></td>
                              <td>{formatBRL(t.valor)}</td>
                            </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan={5}><strong>Valor total</strong></td>
                            <td><strong>{formatBRL(total.reduce((s, t) => s + Number(t.valor || 0), 0))}</strong></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </Secao>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Secao({ id, titulo, vazio, children }) {
  const temConteudo = React.Children.toArray(children).some(Boolean);
  return (
    <div id={id} className="materia-prima-resumo-secao">
      <h4>{titulo}</h4>
      {temConteudo ? children : <div className="materia-prima-resumo-vazio">{vazio}</div>}
    </div>
  );
}

function TabelaItens({ itens, mostrarOrigem = false, mostrarTotal = false }) {
  return (
    <div className="materia-prima-tabela-wrapper">
      <table className="materia-prima-tabela">
        <thead>
          <tr>
            <th>Material</th>
            <th>Quantidade</th>
            <th>Valor</th>
            {mostrarOrigem && <th>Onde</th>}
          </tr>
        </thead>
        <tbody>
          {itens.map((m, i) => (
            <tr key={i}>
              <td>{m.material}</td>
              <td>{formatQtd(m.quantidade)} {m.unidade || ''}</td>
              <td>{formatBRL(m.valor)}</td>
              {mostrarOrigem && <td>{(m.origens || []).join(', ')}</td>}
            </tr>
          ))}
        </tbody>
        {mostrarTotal && (
          <tfoot>
            <tr>
              <td colSpan={2}><strong>Valor total</strong></td>
              <td><strong>{formatBRL(itens.reduce((s, m) => s + Number(m.valor || 0), 0))}</strong></td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
